// @flow
import * as R from "ramda"

import MulticityFormData from "../../records/MulticityFormData"
import { getUrlString } from "common/services/formatters/PlaceFormatter"
import { createAnytime, createTimeToStay, nextDate } from "../../records/SearchDate/utils"
import type SearchFormState from "../../records/SearchFormState"
import type Formatter from "../formatters/Formatter"
import type SearchPlace from "../../records/SearchPlace"
import { DEFAULT_CABIN_CLASS, cabinClassToQuery } from "common/records/CabinClass"

const getString = (place: SearchPlace, preferIds: boolean): string =>
  preferIds ? place.getIds() : getUrlString(place)

const searchFormToQueryPart = (
  searchForm: SearchFormState,
  defaultSearchForm: SearchFormState,
  urlFormatter: Formatter,
  preferIds: boolean = false,
) => {
  const query = {}
  const passengers = searchForm.get("passengers")
  if (!passengers.isPristine()) {
    passengers.entrySeq().forEach(entry => {
      query[entry[0]] = entry[1]
    })
  }
  const cabinClass = searchForm.get("cabinClass")
  if (!R.equals(cabinClass, DEFAULT_CABIN_CLASS)) {
    query.cabinClass = cabinClassToQuery(cabinClass)
  }
  if (searchForm.getMode() === "multicity") {
    const multicitySearchesData = searchForm.multicity
      .map((multicityRow: MulticityFormData, index) => {
        const { origin, destination, outboundDate } = multicityRow
        if (index === 0) {
          // First line
          let firstRowString = ""

          const destinationFilled =
            !destination.isDefault() ||
            destination.getIds() !== defaultSearchForm.destination.getIds()

          firstRowString +=
            destinationFilled ||
            !origin.isDefault() ||
            origin.getIds() !== defaultSearchForm.origin.getIds()
              ? getString(origin, preferIds)
              : "--" // Is default
          firstRowString += "~"
          firstRowString += destinationFilled ? getString(destination, preferIds) : "--" // destination.getIds() === defaultSearchForm.destination.getIds()

          firstRowString += "~"

          firstRowString +=
            urlFormatter.makeSearchDateUrl(outboundDate) ===
              urlFormatter.makeSearchDateUrl(defaultSearchForm.outboundDate) &&
            outboundDate.isDefault
              ? "--" // Is default
              : urlFormatter.makeSearchDateUrl(outboundDate)

          return firstRowString === "--~--~--" ? "-" : firstRowString
        }

        let row = ""

        row +=
          origin.getIds() === searchForm.multicity.get(index - 1).destination.getIds() &&
          origin.isDefault()
            ? "--" // Is default
            : getString(multicityRow.origin, preferIds)
        row += "~"
        row += destination.isDefault()
          ? "--" // Is default
          : getString(destination, preferIds)
        row += "~"
        const outboundDateString = urlFormatter.makeSearchDateUrl(outboundDate)
        row +=
          outboundDateString ===
            urlFormatter.makeSearchDateUrl(
              nextDate(searchForm.multicity.get(index - 1).outboundDate),
            ) && outboundDate.isDefault
            ? "--" // Is default
            : outboundDateString

        return row === "--~--~--" ? "-" : row
      })
      .join(";")

    query.multicity = multicitySearchesData
  }

  if (searchForm.getMode() === "salesman") {
    const salesmanSearchesData = searchForm.salesman
      .get("routes")
      .map((salesmanRow, index) => {
        const {
          destination,
          dateRange,
          timeOfStay,
          dateRangeEnabled,
          timeOfStayEnabled,
        } = salesmanRow
        let firstRowString = ""

        const destinationFilled =
          !destination.isDefault() ||
          destination.getIds() !== defaultSearchForm.destination.getIds()
        if (index === 1 && searchForm.salesman.get("isReturnSame")) {
          firstRowString += "---"
        } else {
          firstRowString += destinationFilled ? destination.getIds() : "--" // destination.getIds() === defaultSearchForm.destination.getIds()
        }
        firstRowString += "~"

        if (index === 1 && !searchForm.salesman.get("setReturnDate")) {
          firstRowString += "---"
        } else if (!dateRangeEnabled) {
          firstRowString += "-"
        } else {
          firstRowString +=
            !dateRange ||
            (urlFormatter.makeSearchDateUrl(dateRange) ===
              urlFormatter.makeSearchDateUrl(defaultSearchForm.outboundDate) &&
              dateRange.isDefault) ||
            (index === 1 &&
              urlFormatter.makeSearchDateUrl(dateRange) ===
                urlFormatter.makeSearchDateUrl(createAnytime()) &&
              dateRange.isDefault) ||
            (index > 1 &&
              urlFormatter.makeSearchDateUrl(dateRange) ===
                urlFormatter.makeSearchDateUrl(
                  nextDate(
                    searchForm.salesman.get("routes").get(index === 2 ? 0 : index - 1).dateRange,
                  ),
                ) &&
              dateRange.isDefault)
              ? "--" // Is default
              : urlFormatter.makeSearchDateUrl(dateRange)
        }
        firstRowString += "~"
        if (!timeOfStayEnabled) {
          firstRowString += "-"
        } else {
          firstRowString +=
            !timeOfStay ||
            index < 2 ||
            (urlFormatter.makeSearchDateUrl(timeOfStay) ===
              urlFormatter.makeSearchDateUrl(createTimeToStay(3, 5, true)) &&
              timeOfStay.isDefault)
              ? "--" // Is default
              : urlFormatter.makeSearchDateUrl(timeOfStay)
        }
        return firstRowString === "--~--~--" ? "-" : firstRowString
      })
      .join(";")
    query.nomad = salesmanSearchesData
  }

  return query
}

export default searchFormToQueryPart
